/**
 * Separa las reglas de negocio de las fallas del sistema.
 *
 * Un servicio lanza `throw new Error('El miembro debe tener un email registrado')` a propósito:
 * es parte de su contrato, no un defecto. Mandarlo al toast reportable genera un issue en Sentry
 * que nadie puede cerrar desde el código y entierra los fallos reales. Estos mensajes van al aviso
 * del formulario, donde quien los ve puede resolverlos arreglando el dato.
 */
const REGLAS_DE_NEGOCIO = [
  'debe tener un email registrado',
  'ya tiene acceso al sistema',
  'ya está registrado en el sistema de autenticación',
  'No tienes permisos para editar la ficha de este miembro',
  'No tienes permisos para aprobar este rol',
  'No puedes resolver una solicitud de rol sobre ti mismo',
  'No puedes resolver una solicitud que pediste tú',
  'Una ficha nueva no puede traer rol ni cuenta; el rol se asigna aprobando una solicitud',
  'Indica si quieres aprobar o rechazar la solicitud',
  'Esta solicitud ya fue resuelta',
  'Este miembro no está en tu jurisdicción',
  'Escribe un comentario para resolver la solicitud',
  'Las observaciones solo las edita la Junta Sacerdotal',
  'No tienes permisos para trasladar a este miembro',
  'El Lumisial de destino debe ser distinto del actual',
  'El Lumisial de destino no está activo',
  'El miembro no tiene un Lumisial de origen válido',
  'La jerarquía del Lumisial de origen o destino está incompleta',
  'El rol actual del miembro ya no coincide con el rol a eliminar',
  'No se encontró la solicitud de rol',
  'No se encontró el rol de la solicitud',
  'No se encontró el miembro',
  'No se puede remover el acceso o cambiar el rol del último Admin Local del Lumisial',
  'No se puede remover el último Admin Local del Lumisial',
  'Ya existe un usuario activo con el email:',
  // Comprobantes (mensajes de la base y del endpoint api/comprobante.ts)
  'necesita su comprobante',
  'adjunta el comprobante ahora',
  'el comprobante no se puede reemplazar',
  'el comprobante no se puede objetar sin observarlo',
  'El comprobante no corresponde a esta entidad',
  'Indica el motivo de la objeción del comprobante',
  'así que no se puede objetar',
  'Indica la fecha del reembolso',
  'faltan comprobantes de',
  'Adjunta el comprobante de la transferencia de la cuota iglesia',
  'Indica qué originó la deuda',
  'Adjunta el documento que origina la deuda',
  'El monto recibido no puede ser mayor que el monto de la deuda',
  'Indica cuánto dinero llegó a la cuenta',
  'Solo un préstamo o crédito en dinero puede traer un depósito',
  'Adjunta el comprobante del aporte',
  'El monto supera lo comprometido',
  'Este Lumisial no tiene su campaña dentro de la campaña general',
  'No tienes permisos para adjuntar comprobantes',
  'El archivo no es una imagen o PDF válido',
  'El PDF supera los 2 MB',
  'El comprobante es demasiado grande',
  'Ya tienes 20 comprobantes sin adjuntar subidos en la última hora',
  'El depósito de comprobantes no tiene espacio disponible',
  // Reglas de movimientos y deudas de la base (T21)
  'Adjunta primero el comprobante para cambiar el mes',
  'El balance de ese mes ya fue enviado: el movimiento no puede cambiar de mes.',
  'Los pagos no se pueden corregir desde la edición de la venta.',
  'La venta anulada no se puede editar.',
  'El gasto anulado no se puede editar.',
  'El total de la venta debe ser positivo, finito y tener hasta dos decimales.',
  'El monto pagado de la venta es inconsistente: requiere revisión contable.',
  'El total de la venta no puede ser menor al monto ya pagado.',
  'La cantidad debe ser positiva y no puede ser menor a lo ya entregado.',
  'El gasto debe tener un importe finito con hasta dos decimales; solo una donación admite cero.',
  'El reembolso bajo la exigencia no se puede desmarcar',
  'El movimiento bajo la exigencia no se puede borrar: anúlalo con un motivo',
  'El movimiento anulado no se puede reactivar directamente',
  'El vínculo de financiamiento solo lo fija la creación de la deuda',
  'El financiamiento no corresponde a esta deuda',
  // Comprobante retirado por la limpieza semanal (H36, código P0001)
  'Este comprobante ya no está disponible: sube el archivo nuevamente',
  // Deudas y movimientos de financiamiento: solo cambian por la operación autorizada (H37 a H39)
  'El origen y su comprobante solo se fijan al crear la deuda',
  'La deuda solo se anula mediante la operación autorizada',
  'Indica el motivo de la anulación',
  'No pudimos confirmar el resultado. Verifica esta operación antes de registrar otra.',
] as const;

export const esErrorDeReglaDeNegocio = (mensaje: string): boolean =>
  REGLAS_DE_NEGOCIO.some((regla) => mensaje.includes(regla));

export const mensajeErrorDeNegocio = (error: unknown): string | null => {
  if (typeof error === 'string') return esErrorDeReglaDeNegocio(error) ? error : null;
  if (!error || typeof error !== 'object') return null;
  const candidato = error as { message?: unknown };
  const mensaje = typeof candidato.message === 'string' ? candidato.message : '';
  return esErrorDeReglaDeNegocio(mensaje) ? mensaje : null;
};
